import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  AccountBalance,
  TrendingUp,
  Payment,
  AttachMoney,
  Info,
  Warning
} from '@mui/icons-material';
import axios from '../utils/axios';
import { formatPrice, getCurrencySymbol } from '../utils/currency';

const Earnings = () => {
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [payoutDialog, setPayoutDialog] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [userCurrency, setUserCurrency] = useState('USD');

  useEffect(() => {
    fetchEarnings();
    fetchConnectStatus();
  }, []);

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/payouts/earnings');
      setEarnings(response.data);
      
      // Get user currency from the response or use default
      if (response.data.currency) {
        setUserCurrency(response.data.currency);
      }
    } catch (error) {
      console.error('Error fetching earnings:', error);
      setError('Failed to load earnings');
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectStatus = async () => {
    try {
      console.log('=== FETCHING CONNECT STATUS ===');
      console.log('Fetching Connect status...');
      
      // First try the debug endpoint to see what's in the database
      try {
        const debugResponse = await axios.get('/api/connect/debug-status');
        console.log('Debug status response:', debugResponse.data);
      } catch (debugError) {
        console.error('Debug status error:', debugError.response?.data);
      }
      
      const response = await axios.get('/api/connect/account-status');
      console.log('Connect status response:', response.data);
      setConnectStatus(response.data);
      console.log('Connect status set to:', response.data);
    } catch (error) {
      console.error('=== ERROR FETCHING CONNECT STATUS ===');
      console.error('Error fetching connect status:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      
      // If we get an error but the response contains account info, use it
      if (error.response?.data?.hasAccount) {
        console.log('Using account info from error response:', error.response.data);
        setConnectStatus(error.response.data);
      } else {
        // Set a default state if we can't fetch status
        setConnectStatus({
          hasAccount: false,
          onboardingComplete: false,
          chargesEnabled: false,
          payoutsEnabled: false
        });
      }
    }
  };

  const createConnectAccount = async () => {
    console.log('=== CREATE CONNECT ACCOUNT DEBUG ===');
    console.log('Create Connect Account button clicked');
    console.log('Current user token:', localStorage.getItem('token'));
    console.log('Current user email:', localStorage.getItem('userEmail'));
    
    try {
      setConnectLoading(true);
      console.log('Setting connectLoading to true');
      console.log('About to send request to /api/connect/create-account');
      
      const response = await axios.post('/api/connect/create-account');
      console.log('Connect account response:', response.data);
      
      if (response.data.message === 'Stripe Connect account already exists') {
        setSuccess('Account found! Setting up payout access...');
        // Use the account status from the response
        setConnectStatus({
          hasAccount: response.data.hasAccount,
          accountId: response.data.accountId,
          onboardingComplete: response.data.onboardingComplete,
          chargesEnabled: response.data.chargesEnabled,
          payoutsEnabled: response.data.payoutsEnabled,
          accountType: response.data.accountType
        });
      } else {
        setSuccess('Stripe Connect account created successfully!');
        await fetchConnectStatus();
      }
    } catch (error) {
      console.error('=== ERROR IN CREATE CONNECT ACCOUNT ===');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Error response statusText:', error.response?.statusText);
      
      // Handle specific error cases
      if (error.response?.data?.error === 'Stripe Connect account already exists') {
        setSuccess('Account found! Setting up payout access...');
        
        // Set the connect status to show the account exists
        setConnectStatus({
          hasAccount: true,
          accountId: error.response.data.accountId,
          onboardingComplete: false, // We don't know the status yet
          chargesEnabled: false,
          payoutsEnabled: false
        });
        
        // Refresh the connect status to get the actual status
        setTimeout(async () => {
          await fetchConnectStatus();
        }, 1000);
      } else {
        const errorData = error.response?.data;
        let errorMessage = errorData?.error || 'Failed to create Connect account';
        
        // Handle platform setup required error with details
        if (errorData?.details) {
          const details = errorData.details;
          errorMessage = `${errorMessage}\n\n${details.message}\n\nAction Required: ${details.action}\n\n${details.instructions}`;
          
          // If there's a URL, we could potentially open it or show it
          if (details.url) {
            console.log('Platform setup URL:', details.url);
          }
        }
        
        setError(errorMessage);
      }
    } finally {
      console.log('Setting connectLoading to false');
      setConnectLoading(false);
    }
  };

  const resetAccount = async () => {
    try {
      setConnectLoading(true);
      setError(null);
      await axios.post('/api/connect/update-account', { reset: true });
      setSuccess('Account reset. Please delete the old account in Stripe Dashboard if it exists, then click "Create Payout Account".');
      // Clear the status to show "Create Account" button
      setConnectStatus({ hasAccount: false });
      await fetchConnectStatus(); // Refresh status
    } catch (error) {
      console.error('Error resetting account:', error);
      setError(error.response?.data?.error || 'Failed to reset account');
    } finally {
      setConnectLoading(false);
    }
  };

  const startOnboarding = async () => {
    try {
      setConnectLoading(true);
      setError(null);
      const response = await axios.post('/api/connect/create-onboarding-link');
      console.log('Onboarding link created:', response.data);
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        setError('No URL returned from server');
        setConnectLoading(false);
      }
    } catch (error) {
      console.error('Error creating onboarding link:', error);
      const errorMessage = error.response?.data?.error || 'Failed to start onboarding';
      const errorDetails = error.response?.data?.details || '';
      
      // Check if error indicates account is not linked/accessible
      if (errorMessage.includes('not connected to your platform') || 
          errorMessage.includes('does not exist') ||
          errorDetails.includes('not connected')) {
        // Mark account as invalid so reset button shows
        setConnectStatus(prev => ({ ...prev, accountInvalid: true }));
        setError(`${errorMessage}. Please use the "Reset & Create New Account" button below.`);
      } else {
        setError(errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage);
      }
      setConnectLoading(false);
    }
  };

  const handlePayoutRequest = async () => {
    if (!payoutAmount || parseFloat(payoutAmount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (parseFloat(payoutAmount) > earnings.availableAmount) {
      setError(`Amount cannot exceed available earnings (${formatPrice(earnings.availableAmount, userCurrency)})`);
      return;
    }

    try {
      setPayoutLoading(true);
      await axios.post('/api/payouts/request', { amount: parseFloat(payoutAmount) });
      setSuccess('Payout processed successfully!');
      setPayoutDialog(false);
      setPayoutAmount('');
      fetchEarnings(); // Refresh earnings
      fetchConnectStatus(); // Refresh connect status
    } catch (error) {
      console.error('Error requesting payout:', error);
      setError(error.response?.data?.error || 'Failed to request payout');
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleDebugEarnings = async () => {
    try {
      const response = await axios.get('/api/payouts/earnings/debug');
      console.log('=== EARNINGS DEBUG DATA ===');
      console.log(JSON.stringify(response.data, null, 2));
      alert('Debug data logged to console! Check the browser console (F12) for details.');
    } catch (error) {
      console.error('Error fetching debug data:', error);
      setError('Failed to fetch debug data');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h6">Loading earnings...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Earnings Dashboard
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Total Earnings Card */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccountBalance color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Earnings</Typography>
                </Box>
                <Typography variant="h4" color="primary">
                  {formatPrice(earnings?.totalEarnings || 0, userCurrency)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All time earnings from bookings
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Available Amount Card */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AttachMoney color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6">Available for Payout</Typography>
                </Box>
                <Typography variant="h4" color="success.main">
                  {formatPrice(earnings?.availableAmount || 0, userCurrency)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Amount you can withdraw
                </Typography>
                {earnings?.stripeBalance ? (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Stripe Balance:
                    </Typography>
                    <Typography variant="body2" color="primary" fontWeight="medium">
                      {formatPrice(earnings.stripeBalance.available, earnings.stripeBalance.currency)}
                    </Typography>
                    {earnings.stripeBalance.pending > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Pending: {formatPrice(earnings.stripeBalance.pending, earnings.stripeBalance.currency)}
                      </Typography>
                    )}
                  </Box>
                ) : earnings?.stripeBalanceError ? (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Alert 
                      severity="warning" 
                      icon={<Warning />}
                      sx={{ py: 0.5 }}
                    >
                      <Typography variant="caption" display="block">
                        Showing calculated amount (Stripe balance unavailable)
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        This may differ from your actual Stripe balance due to refunds or disputes.
                      </Typography>
                    </Alert>
                  </Box>
                ) : (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Alert 
                      severity="info" 
                      icon={<Info />}
                      sx={{ py: 0.5 }}
                    >
                      <Typography variant="caption" display="block">
                        Showing calculated amount
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Based on confirmed bookings minus payouts
                      </Typography>
                    </Alert>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Total Bookings Card */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUp color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Bookings</Typography>
                </Box>
                <Typography variant="h4" color="info.main">
                  {earnings?.totalBookings || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Confirmed bookings
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Payouts Card */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Payment color="warning" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Payouts</Typography>
                </Box>
                <Typography variant="h4" color="warning.main">
                  {formatPrice(earnings?.totalPayouts || 0, userCurrency)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Amount withdrawn
                </Typography>
              </CardContent>
            </Card>
          </Grid>

        </Grid>

        {/* Stripe Connect Section */}
        <Paper sx={{ mt: 4, p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Payout Account Setup
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Set up your bank account to receive payouts from your gym bookings.
          </Typography>
          
          {console.log('Rendering Connect section, connectStatus:', connectStatus)}
          {connectStatus?.accountInvalid ? (
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  Account Not Linked to Platform
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  The account in your database is not connected to your Stripe platform. Please reset and create a new account.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={resetAccount}
                  disabled={connectLoading}
                  sx={{ mt: 1 }}
                >
                  {connectLoading ? 'Resetting...' : 'Reset & Create New Account'}
                </Button>
              </Alert>
            </Box>
          ) : !connectStatus?.hasAccount ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                You need to create a Stripe Connect account to receive payouts.
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  console.log('Button clicked!');
                  createConnectAccount();
                }}
                disabled={connectLoading}
                size="large"
              >
                {connectLoading ? 'Creating Account...' : 'Create Payout Account'}
              </Button>
            </Box>
          ) : !connectStatus?.onboardingComplete ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Complete your account setup to enable payouts.
              </Typography>
              <Button
                variant="contained"
                onClick={startOnboarding}
                disabled={connectLoading}
                size="large"
              >
                {connectLoading ? 'Loading...' : 'Complete Setup'}
              </Button>
            </Box>
          ) : connectStatus?.isRestricted ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  Account Restricted
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Your account needs additional verification or information to enable payouts.
                </Typography>
                {connectStatus?.requirements && (
                  <Box sx={{ mt: 2 }}>
                    {connectStatus.requirements.currentlyDue && connectStatus.requirements.currentlyDue.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          Required Information:
                        </Typography>
                        <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 1 }}>
                          {connectStatus.requirements.currentlyDue.map((req, idx) => (
                            <li key={idx}>{req.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</li>
                          ))}
                        </Typography>
                      </Box>
                    )}
                    {connectStatus.requirements.pastDue && connectStatus.requirements.pastDue.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight="medium" color="error" gutterBottom>
                          Past Due Requirements:
                        </Typography>
                        <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 1 }}>
                          {connectStatus.requirements.pastDue.map((req, idx) => (
                            <li key={idx}>{req.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</li>
                          ))}
                        </Typography>
                      </Box>
                    )}
                    {connectStatus.requirements.disabledReason && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        Reason: {connectStatus.requirements.disabledReason}
                      </Typography>
                    )}
                  </Box>
                )}
                <Button
                  variant="contained"
                  onClick={startOnboarding}
                  disabled={connectLoading}
                  size="large"
                  sx={{ mt: 2 }}
                >
                  {connectLoading ? 'Loading...' : 'Complete Required Information'}
                </Button>
              </Alert>
            </Box>
          ) : !connectStatus?.payoutsEnabled ? (
            <Box>
              <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                Your account setup is pending approval. Payouts will be enabled once approved.
              </Typography>
              <Button
                variant="outlined"
                onClick={startOnboarding}
                disabled={connectLoading}
                size="large"
              >
                {connectLoading ? 'Loading...' : 'Update Account Details'}
              </Button>
            </Box>
          ) : (
            <Box>
              {connectStatus?.accountInvalid ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Account Not Linked to Platform
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    The account in your database is not connected to your Stripe platform. Please reset and create a new account.
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={resetAccount}
                    disabled={connectLoading}
                    sx={{ mt: 1 }}
                  >
                    {connectLoading ? 'Resetting...' : 'Reset & Create New Account'}
                  </Button>
                </Alert>
              ) : connectStatus?.platformSetupRequired ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Stripe Connect Platform Setup Required
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Your Stripe account needs to be configured as a Connect platform to enable full functionality.
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    href="https://dashboard.stripe.com/account/applications/settings"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ mt: 1 }}
                  >
                    Enable Stripe Connect
                  </Button>
                </Alert>
              ) : (
                <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
                  ✅ Your payout account is set up and ready to receive payments!
                </Typography>
              )}
              <Button
                variant="outlined"
                onClick={startOnboarding}
                disabled={connectLoading || connectStatus?.accountInvalid}
                size="large"
              >
                {connectLoading ? 'Loading...' : 'Manage Account'}
              </Button>
            </Box>
          )}
        </Paper>

        {/* Payout Section */}
        <Paper sx={{ mt: 4, p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Request Payout
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Withdraw your earnings to your connected bank account.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Payment />}
              onClick={() => setPayoutDialog(true)}
              disabled={
                !earnings?.availableAmount || 
                earnings.availableAmount <= 0 ||
                !connectStatus?.payoutsEnabled
              }
              size="large"
            >
              Request Payout
            </Button>
            
            <Button
              variant="outlined"
              onClick={handleDebugEarnings}
              size="large"
            >
              Debug Earnings
            </Button>
          </Box>
          
          {(!earnings?.availableAmount || earnings.availableAmount <= 0) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No funds available for payout
            </Typography>
          )}
          
          {earnings?.availableAmount > 0 && !connectStatus?.payoutsEnabled && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
              {(() => {
                console.log('=== PAYOUT MESSAGE DEBUG ===');
                console.log('connectStatus:', connectStatus);
                console.log('hasAccount:', connectStatus?.hasAccount);
                console.log('onboardingComplete:', connectStatus?.onboardingComplete);
                console.log('payoutsEnabled:', connectStatus?.payoutsEnabled);
                console.log('isRestricted:', connectStatus?.isRestricted);
                
                if (!connectStatus?.hasAccount) {
                  return 'Create a payout account above to request payouts';
                } else if (connectStatus?.isRestricted) {
                  return 'Your account is restricted. Please complete the required information above to enable payouts.';
                } else if (!connectStatus?.onboardingComplete) {
                  return 'Complete your account setup above to enable payouts';
                } else {
                  return 'Your account setup is pending approval. Payouts will be enabled once approved.';
                }
              })()}
            </Typography>
          )}
        </Paper>
      </Box>

      {/* Payout Dialog */}
      <Dialog open={payoutDialog} onClose={() => setPayoutDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Payout</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Available for payout: <strong>{formatPrice(earnings?.availableAmount || 0, userCurrency)}</strong>
          </Typography>
          <TextField
            fullWidth
            label={`Payout Amount (${getCurrencySymbol(userCurrency)})`}
            type="number"
            value={payoutAmount}
            onChange={(e) => setPayoutAmount(e.target.value)}
            inputProps={{ min: 0, max: earnings?.availableAmount || 0, step: 0.01 }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayoutDialog(false)}>Cancel</Button>
          <Button 
            onClick={handlePayoutRequest} 
            variant="contained"
            disabled={payoutLoading || !payoutAmount || parseFloat(payoutAmount) <= 0}
          >
            {payoutLoading ? 'Processing...' : 'Request Payout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Earnings; 